/**
 * DryRunCtx — dashboard-local "dry-run mode" state (ADR-0109, ADR-0117).
 *
 * Dry-run is a *review* mode: when on, the operator is told that rule changes
 * are recorded but NOT enforced, and a "would have blocked" shadow log is
 * surfaced so they can review impact before publishing. Per the reference
 * (`Foyer_Dashboard_CURRENT.jsx:141`).
 *
 * SAFETY INVARIANT (the whole point of the feature): the dry-run flag is
 * purely dashboard-local UI state. It is NEVER passed to
 * `api.policy.publish()`/enforcement. Enforcement only changes when the
 * operator explicitly publishes a draft; dry-run cannot enable nor disable
 * enforcement. This context therefore exposes only `enabled` / `setEnabled`
 * and a read-only `shadow` list — nothing that flows into publish.
 *
 * ADR-0117 Slice A: when the toggle flips ON for a site with a draft, this
 * context polls `api.policy.loadShadowLog(site_id)` (initial fetch + one
 * re-fetch on window focus) and surfaces the results. The producer (Slice B)
 * has not shipped yet, so the API returns `{ draft_fingerprint, decisions:
 * [] }` for sites with a draft and `{ draft_fingerprint: null, decisions: []
 * }` otherwise. The panel renders the same honest-empty state either way
 * until Slice B starts populating rows. Toggle OFF stops polling and clears
 * the local cache — read state is not persisted across toggles.
 *
 * Persistence: per-site, in `sessionStorage` (`sill:dry_run:<site_id>`), so a
 * dry-run session survives a reload but does not silently persist forever.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { ShadowDecision } from '@sill/types';

import { useOptionalApi } from '../api/api-context.js';
import { useSites } from './providers.js';

// Re-export the canonical type so existing dashboard imports
// (`from '../app/dry-run-context.js'`) keep working without a churn
// commit. New code should import directly from `@sill/types`.
export type { ShadowDecision } from '@sill/types';

export interface DryRunCtxValue {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  shadow: ShadowDecision[];
  /**
   * ADR-0117 Slice C honesty item — the draft fingerprint carried on the
   * MOST RECENT poll. `null` when the site has no draft (so the panel
   * can render its "no draft — nothing to shadow" branch) and `null`
   * while the toggle is OFF (`enabled === false`).
   */
  draft_fingerprint: string | null;
  /**
   * The draft fingerprint as of the FIRST poll after this provider's
   * toggle flipped ON (or after the active site changed). The panel
   * surfaces an unobtrusive hint when this diverges from
   * `draft_fingerprint`, so the operator sees that their edit
   * invalidated the prior shadow window. `null` until the first
   * successful poll completes. Reset to `null` on toggle-OFF or
   * site-change.
   */
  initial_draft_fingerprint: string | null;
  /**
   * ADR-0120 §2.1(c) honest loading/error state. `'loading'` while the
   * first fetch after toggle-ON is in flight (the panel renders a
   * "Loading shadow log…" line instead of the "no decisions yet"
   * empty copy, which would be misleading on first paint). `'error'`
   * when the last attempt threw — the panel renders a non-blocking
   * "Could not load shadow log" retry pill. `'idle'` while the toggle
   * is OFF; `'ok'` on a successful fetch. Per ADR-0120 §8 Slice 2 this
   * REPLACES the prior silent error swallow at lines 148-193.
   */
  status: 'idle' | 'loading' | 'ok' | 'error';
  /**
   * Manually re-trigger the shadow-log fetch. Wires the "Retry" button
   * on the error pill. No-op when the toggle is OFF or no site is
   * active.
   */
  retry: () => void;
}

/**
 * Module-local React context. Exported as a NAMED export only so the
 * Slice C honesty-item tests can wrap a child tree in a controlled-value
 * provider (`<DryRunCtx.Provider value={...}>`) without going through
 * `DryRunProvider`'s poll machinery. Production code should use
 * `useDryRun()` + `DryRunProvider`; tests are the only legitimate
 * `DryRunCtx.Provider` consumer outside this file.
 */
export const DryRunCtx = createContext<DryRunCtxValue>({
  enabled: false,
  setEnabled: () => {
    /* no-op default — real value provided by DryRunProvider */
  },
  shadow: [],
  draft_fingerprint: null,
  initial_draft_fingerprint: null,
  status: 'idle',
  retry: () => {
    /* no-op default */
  },
});

export function useDryRun(): DryRunCtxValue {
  return useContext(DryRunCtx);
}

function storageKey(siteId: string): string {
  return `sill:dry_run:${siteId}`;
}

function readEnabled(siteId: string | undefined): boolean {
  if (!siteId || typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(storageKey(siteId)) === '1';
  } catch {
    // sessionStorage may be unavailable (privacy mode) — default OFF.
    return false;
  }
}

export function DryRunProvider({ children }: { children: ReactNode }): JSX.Element {
  // Provider-optional so tests + tree mounts that don't wire an
  // `<ApiProvider>` (the existing per-site toggle test) still work.
  // Falling back to `null` means we skip the `loadShadowLog` poll;
  // the panel renders its honest-empty state, which is the right
  // posture in that environment anyway.
  const api = useOptionalApi();
  const { activeSite } = useSites();
  const siteId = activeSite?.site_id;
  // `tick` forces a re-read of sessionStorage after a write; `siteId` re-reads
  // when the active site changes (dry-run is per-site).
  const [tick, setTick] = useState(0);
  const [shadow, setShadow] = useState<ShadowDecision[]>([]);
  // ADR-0117 Slice C — track the fingerprint server-side returned on
  // each poll so the panel can render the "draft fingerprint changed"
  // hint when the merchant's edit invalidates the prior shadow window.
  // `current` is the fingerprint on the MOST RECENT poll; `initial` is
  // the first one observed after the toggle flipped ON. Both reset to
  // `null` on toggle-OFF or site-change.
  const [draftFingerprint, setDraftFingerprint] = useState<string | null>(null);
  const [initialDraftFingerprint, setInitialDraftFingerprint] = useState<
    string | null
  >(null);
  // ADR-0120 §2.1(c) — replace the silent error swallow with an exposed
  // loading/ok/error status. `retryTick` is a counter the `retry()`
  // callback increments to re-run the fetch effect.
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>(
    'idle',
  );
  const [retryTick, setRetryTick] = useState(0);

  const enabled = useMemo(() => readEnabled(siteId), [siteId, tick]);

  const setEnabled = useCallback(
    (next: boolean): void => {
      if (!siteId || typeof window === 'undefined') return;
      try {
        window.sessionStorage.setItem(storageKey(siteId), next ? '1' : '0');
      } catch {
        // best-effort; if storage is blocked the toggle simply won't persist
      }
      setTick((t) => t + 1);
    },
    [siteId],
  );

  // ADR-0117 Slice A + ADR-0120 §2.1(c) — poll `loadShadowLog` while
  // the toggle is ON. Strategy: initial fetch on toggle-flip-or-site-
  // change, plus one re-fetch on window focus. Live streaming (SSE) is
  // deferred per ADR-0117 "Alternatives considered" — the operator's
  // "review impact" workflow lasts minutes, not seconds.
  //
  // Errors are no longer silently swallowed (the prior behavior at
  // these lines collapsed every failure to an empty list, which made
  // the panel indistinguishable from "no rows yet" on a 503). We
  // surface a `status` field instead — the panel renders a visible
  // but non-blocking "Could not load shadow log" pill on error and a
  // "Loading…" line on the first fetch, while the rest of the
  // dashboard stays functional. A transient 503 still doesn't break
  // the dashboard; it now just says so honestly.
  useEffect(() => {
    if (!enabled || !siteId || api === null) {
      setShadow([]);
      setDraftFingerprint(null);
      setInitialDraftFingerprint(null);
      setStatus('idle');
      return;
    }
    let cancelled = false;
    // Closure-local — the first successful poll on this (siteId,
    // enabled) effect run seeds `initialDraftFingerprint`. Subsequent
    // polls (focus refetch) only update `draftFingerprint`, so the
    // panel can diff against the original. We pin the initial in a
    // ref-shaped local because React state writes in the same tick
    // wouldn't be visible across the next focus invocation.
    let initialSeeded = false;
    let firstFetch = true;
    const fetchOnce = async (): Promise<void> => {
      // Only show the "loading" spinner on the FIRST fetch of an
      // enabled session — focus-refetches stay quiet so the panel
      // doesn't flash every time the operator switches tabs.
      if (firstFetch) {
        setStatus('loading');
      }
      try {
        const res = await api.policy.loadShadowLog(siteId);
        if (cancelled) return;
        setShadow(res.decisions);
        setDraftFingerprint(res.draft_fingerprint);
        if (!initialSeeded) {
          initialSeeded = true;
          setInitialDraftFingerprint(res.draft_fingerprint);
        }
        setStatus('ok');
      } catch {
        if (!cancelled) {
          // Keep prior rows in place so a transient focus-refetch
          // error doesn't blank the panel out from under the operator.
          // The status pill makes the failure visible without hiding
          // the data the previous successful fetch surfaced.
          setStatus('error');
        }
      } finally {
        firstFetch = false;
      }
    };
    void fetchOnce();
    const onFocus = (): void => {
      void fetchOnce();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
    }
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [enabled, siteId, api, retryTick]);

  const retry = useCallback((): void => {
    // Bump the dep — the effect re-runs and re-fetches. Cheap and
    // race-safe (the prior effect's `cancelled = true` runs first).
    setRetryTick((t) => t + 1);
  }, []);

  const value = useMemo<DryRunCtxValue>(
    () => ({
      enabled,
      setEnabled,
      shadow,
      draft_fingerprint: draftFingerprint,
      initial_draft_fingerprint: initialDraftFingerprint,
      status,
      retry,
    }),
    [
      enabled,
      setEnabled,
      shadow,
      draftFingerprint,
      initialDraftFingerprint,
      status,
      retry,
    ],
  );

  return <DryRunCtx.Provider value={value}>{children}</DryRunCtx.Provider>;
}
